import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { formatDealPrice } from "@/lib/dealPrice";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, ShieldCheck, LogIn } from "lucide-react";

/**
 * Public redeem page — the QR code destination.
 * Anon users see the voucher summary + a Sign-in CTA.
 * Signed-in users (must be the deal vendor or admin) tap Redeem.
 */
export default function RedeemVoucherPage() {
  const { voucherId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [result, setResult] = useState(null); // { ok, deal_title, customer_name, redeemed_at, code }

  const q = useQuery({
    queryKey: ["voucher-public", voucherId],
    queryFn: async () => (await sharedApi.get(`/classifieds/deal-vouchers/${voucherId}`)).data,
    enabled: !!voucherId,
    retry: 0,
  });

  const redeemMut = useMutation({
    mutationFn: async () => (await sharedApi.post(`/classifieds/deal-vouchers/${voucherId}/redeem`)).data,
    onSuccess: (data) => {
      setResult(data);
      toast.success("Voucher redeemed");
      q.refetch();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || "Could not redeem this voucher";
      toast.error(String(detail));
    },
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (q.isLoading) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <XCircle className="w-12 h-12 mx-auto text-red-500" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Voucher not found</h1>
        <p className="mt-2 text-slate-500 text-sm">The voucher may have expired or the link is broken.</p>
        <Link to="/deals" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">Browse deals</Link>
      </div>
    );
  }

  const v = q.data;
  const isRedeemed = v.status === "redeemed" || !!result?.redeemed_at;
  const isExpired = v.status === "expired";

  return (
    <div className="max-w-md mx-auto px-4 py-10 md:py-16">
      <Seo title={`Redeem ${v.code}`} noindex />
      <Breadcrumbs items={[{ label: "Redeem voucher" }]} />

      <div className="mt-6 rounded-3xl bg-white shadow-card overflow-hidden border border-soft" data-testid="redeem-card">
        {/* Status header */}
        <div className={`px-6 py-6 text-center ${isRedeemed ? "bg-emerald-50" : isExpired ? "bg-slate-100" : "bg-[color:var(--brand-blue-wash)]"}`}>
          <div className="inline-flex w-14 h-14 rounded-full items-center justify-center bg-white shadow-sm">
            {isRedeemed ? (
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            ) : isExpired ? (
              <Clock className="w-8 h-8 text-slate-400" />
            ) : (
              <ShieldCheck className="w-8 h-8 text-[color:var(--brand-blue)]" />
            )}
          </div>
          <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500" data-testid="redeem-status">
            {isRedeemed ? "Already redeemed" : isExpired ? "Expired" : "Ready to redeem"}
          </div>
          <div className="mt-1 font-display text-2xl font-black text-slate-900" data-testid="redeem-code">{v.code}</div>
        </div>

        {/* Deal info */}
        <div className="px-6 py-5 space-y-3 text-sm">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Deal</div>
            <div className="mt-0.5 text-slate-900 font-semibold" data-testid="redeem-deal-title">{v.deal_title}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Customer</div>
              <div className="mt-0.5 text-slate-900" data-testid="redeem-customer">{v.customer_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Merchant</div>
              <div className="mt-0.5 text-slate-900">{v.merchant || "—"}</div>
            </div>
          </div>
          {v.expires_at && !isRedeemed && (
            <div className="text-xs text-slate-500">
              Expires {new Date(v.expires_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
            </div>
          )}
          {isRedeemed && v.redeemed_at && (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="redeem-success-line">
              Serve the customer — voucher was redeemed on {new Date(v.redeemed_at).toLocaleString("en-NZ")}.
            </div>
          )}
        </div>

        {/* Action row */}
        <div className="px-6 pb-6">
          {!user ? (
            <>
              <div className="mb-3 text-xs text-slate-500">Merchants must sign in to redeem.</div>
              <button
                type="button"
                data-testid="redeem-login-btn"
                onClick={() => nav(`/login?next=${encodeURIComponent(loc.pathname)}`)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold bg-slate-900 hover:bg-slate-800 text-white transition-colors"
              >
                <LogIn className="w-4 h-4" /> Sign in to redeem
              </button>
            </>
          ) : isRedeemed ? (
            <button
              type="button"
              disabled
              data-testid="redeem-done-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold bg-emerald-500 text-white opacity-90"
            >
              <CheckCircle2 className="w-4 h-4" /> Redeemed
            </button>
          ) : (
            <>
              {(() => {
                const fmt = formatDealPrice(v);
                return fmt.collectLine ? (
                  <div
                    className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-center text-sm font-bold text-amber-900"
                    data-testid="redeem-collect-line"
                  >
                    {fmt.collectLine}
                  </div>
                ) : null;
              })()}
              <button
                type="button"
                data-testid="redeem-submit-btn"
                onClick={() => redeemMut.mutate()}
                disabled={redeemMut.isPending || isExpired}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white shadow-card disabled:opacity-60 transition-colors"
              >
                {redeemMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {isExpired ? "Voucher expired" : "Redeem voucher"}
              </button>
            </>
          )}
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-slate-400">
        Only the deal vendor (or an admin) can redeem. One use per voucher.
      </p>
    </div>
  );
}
